import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { IS_NATIVE_APP } from "../config/appTarget.js";

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js" | "consent",
      targetId?: string | Date,
      config?: Record<string, any>,
    ) => void;
  }
}

interface PageViewEvent {
  page_title?: string;
  page_location?: string;
  page_path?: string;
  [key: string]: any;
}

export const useGoogleAnalytics = () => {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    if (IS_NATIVE_APP) return;
    if (typeof window !== "undefined" && window.gtag) {
      // Send page_view event for SPA navigation
      const pageViewData: PageViewEvent = {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search,
      };

      window.gtag("event", "page_view", pageViewData);
    }
  }, [location]);

  // Custom event tracking function
  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, parameters);

      if (process.env.NODE_ENV === "development") {
        console.log("GA Event:", eventName, parameters);
      }
    }
  };

  // Predefined tracking functions for common events
  const trackDogView = (dogId: string, dogName: string) => {
    trackEvent("view_dog", {
      dog_id: dogId,
      dog_name: dogName,
      event_category: "engagement",
    });
  };

  const trackWishlistAction = (
    action: "add" | "remove",
    dogId: string,
    dogName: string,
  ) => {
    trackEvent("wishlist_action", {
      action,
      dog_id: dogId,
      dog_name: dogName,
      event_category: "engagement",
    });
  };

  const trackSearch = (searchTerm: string, filters?: Record<string, any>) => {
    trackEvent("search", {
      search_term: searchTerm,
      ...filters,
      event_category: "search",
    });
  };

  const trackAdoptionInterest = (
    dogId: string,
    contactMethod: "email" | "phone",
  ) => {
    trackEvent("adoption_interest", {
      dog_id: dogId,
      contact_method: contactMethod,
      event_category: "conversion",
    });
  };

  const trackPhotoUpload = (
    photoCount: number,
    uploadMethod: "drag" | "click",
  ) => {
    trackEvent("photo_upload", {
      photo_count: photoCount,
      upload_method: uploadMethod,
      event_category: "engagement",
    });
  };

  return {
    trackEvent,
    trackDogView,
    trackWishlistAction,
    trackSearch,
    trackAdoptionInterest,
    trackPhotoUpload,
  };
};

export default useGoogleAnalytics;
